import React, { useEffect, useState, useCallback, useRef } from 'react';
import NotificationImportantOutlinedIcon from '@mui/icons-material/NotificationImportantOutlined';
import NotificationLog from "./NotificationLog";
import List from "@mui/material/List";
import CardHeader from "@mui/material/CardHeader";
import Button from "@mui/material/Button";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Divider from "@mui/material/Divider";
import JumboIconButton from "@jumbo/components/JumboIconButton";
import JumboDdPopover from "@jumbo/components/JumboDdPopover";
import Div from "@jumbo/shared/Div";
import {CardActions, ThemeProvider, CircularProgress, Box} from "@mui/material";
import useJumboHeaderTheme from "@jumbo/hooks/useJumboHeaderTheme";
import useJumboTheme from "@jumbo/hooks/useJumboTheme";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAPI } from "app/hooks/useApi";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import moment from "moment";

const NotificationsDropdown = () => {
    const {theme} = useJumboTheme();
    const {headerTheme} = useJumboHeaderTheme();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { makeRequest } = useAPI();
    const { authUser } = useJumboAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const isReady = i18n.isInitialized;
    const makeRequestRef = useRef(makeRequest);
    const hasFetchedRef = useRef(false);

    const isAdmin = authUser?.role === 'ADMIN';

    useEffect(() => {
        makeRequestRef.current = makeRequest;
    }, [makeRequest]);

    const fetchNotifications = useCallback(async () => {
        if (hasFetchedRef.current || !isAdmin) {
            return;
        }

        hasFetchedRef.current = true;
        setLoading(true);
        
        try {
            const response = await makeRequestRef.current(
                `/logs-downloads-rpis-weekly?page=0&perPage=5&sortBy=createdAt&sortOrder=desc`,
                "GET"
            );

            if (response && response.status === 200 && response.data?.logs) {
                const sevenDaysAgo = moment().subtract(7, 'days').startOf('day');
                const filteredLogs = response.data.logs.filter(log => {
                    const logDate = moment(log.date || log.createdAt);
                    return logDate.isAfter(sevenDaysAgo);
                });
                
                setLogs(filteredLogs);
            } else {
                setLogs([]);
            }
        } catch (error) {
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        if (!hasFetchedRef.current && isAdmin) {
            fetchNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin]);

    const handleViewAll = () => {
        navigate("/logs-rpis");
    };

    return (
        <ThemeProvider theme={theme}>
            <JumboDdPopover
                triggerButton={
                    <ThemeProvider theme={headerTheme}>
                        <JumboIconButton elevation={25}>
                            <NotificationImportantOutlinedIcon sx={{fontSize: '1.25rem'}}/>
                        </JumboIconButton>
                    </ThemeProvider>
                }
                disableInsideClick
            >
                <Div sx={{width: 360, maxWidth: '100%'}}>
                    <CardHeader
                        title={isReady ? t("notifications.title", "Notificações") : "Notificações"}
                    />
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List disablePadding>
                            {logs.length > 0 ? (
                                logs.map((log, index) => (
                                    <NotificationLog key={log.id || index} item={log} />
                                ))
                            ) : (
                                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                                    {isReady ? t("notifications.empty", "Sem notificações") : "Sem notificações"}
                                </Box>
                            )}
                        </List>
                    )}
                    <Divider/>
                    <CardActions sx={{justifyContent: 'center'}}>
                        <Button 
                            sx={{
                                textTransform: "none", 
                                fontWeight: 'normal', 
                                cursor: 'default',
                                '&:hover': { 
                                    bgcolor: 'transparent',
                                    cursor: 'default'
                                }
                            }}
                            size={"small"} 
                            variant="text" 
                            endIcon={<ArrowForwardIcon/>} 
                            disableRipple
                            onClick={handleViewAll}
                        >
                            {isReady ? t("notifications.viewAll", "Ver Tudo") : "Ver Tudo"}
                        </Button>
                    </CardActions>
                </Div>
            </JumboDdPopover>
        </ThemeProvider>
    );
};

export default NotificationsDropdown;
